import { Helmet } from "react-helmet-async";
import { Box, Typography, useTheme } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  clearDashboardTableState,
  DASHBOARD_RESET_EVENT,
} from "../../utils/dashboardTableState";
import { APP_TITLE } from "../../constant/index.js";
import DashboardStats from "../../modules/Dashboard/DashboardStats.jsx";
import CandidateInterviewsTable from "../../modules/Dashboard/CandidateInterviewsTable.jsx";
import RescheduleInterviewDialog from "../../modules/Dashboard/RescheduleInterviewDialog.jsx";
import { AxiosService } from "../../services/axios/axiosService.js";
import toast from "react-hot-toast";

const Dashboard = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [interviewsData, setInterviewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableResetKey, setTableResetKey] = useState(0);

  const fetchInterviews = useCallback(async ({ background = false } = {}) => {
    try {
      if (!background) setLoading(true);
      const response = await AxiosService.get("/api/interview");
      setInterviewsData(response);
    } catch {
      setInterviewsData([]);
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  const resetDashboardView = useCallback(
    (options = {}) => {
      const { background = true } = options;
      clearDashboardTableState();
      setTableResetKey((key) => key + 1);
      fetchInterviews({ background });
    },
    [fetchInterviews],
  );

  useEffect(() => {
    fetchInterviews();
  }, [fetchInterviews]);

  useEffect(() => {
    if (!location.state?.resetDashboard) return;
    resetDashboardView({ background: false });
    navigate("/dashboard", { replace: true, state: {} });
  }, [location.state?.resetDashboard, resetDashboardView, navigate]);

  useEffect(() => {
    const onLogoReset = () => resetDashboardView({ background: true });
    window.addEventListener(DASHBOARD_RESET_EVENT, onLogoReset);
    return () => window.removeEventListener(DASHBOARD_RESET_EVENT, onLogoReset);
  }, [resetDashboardView]);

  const onReschedule = (candidateData) => {
    setSelectedCandidate(candidateData);
    setRescheduleDialogOpen(true);
  };

  const handleCloseReschedule = () => {
    setRescheduleDialogOpen(false);
    //setSelectedCandidate(null);
  };

  return (
    <>
      <Helmet>
        <title>Dashboard | {APP_TITLE}</title>
        <meta
          name="Dashboard"
          content="Dashboard to monitor interview progress and AI results"
        />
      </Helmet>

      <Box
        sx={{
          p: 3.5,
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, color: theme.palette.text.primary,  }}
          >
            Dashboard
          </Typography>
        </Box>

        <DashboardStats interviewCount={interviewsData}/>

        <CandidateInterviewsTable
          key={tableResetKey}
          interviews={interviewsData}
          loading={loading}
          onReschedule={onReschedule}
        />
      </Box>

      <RescheduleInterviewDialog
        open={rescheduleDialogOpen}
        onClose={handleCloseReschedule}
        onRescheduleSuccess={fetchInterviews}
        candidateData={selectedCandidate}
      />
    </>
  );
};

export default Dashboard;
